import { useEffect, useRef } from "react";
import { useLoadScript } from "@react-google-maps/api";

// Keep the libraries array constant across renders — useLoadScript throws
// "Performance warning" if the reference changes.
const LIBRARIES = ["places"];

// Normalises addressComponents from the new Places API
// ({ types, longText, shortText }) into the legacy shape
// ({ types, long_name, short_name }) that our downstream code parses.
const normalizeAddressComponents = (components = []) =>
  components.map((c) => ({
    types: c.types || [],
    long_name: c.longText ?? c.long_name ?? "",
    short_name: c.shortText ?? c.short_name ?? "",
  }));

const normalizePlace = (place) => {
  if (!place) return null;
  const loc = place.location;
  return {
    address_components: normalizeAddressComponents(place.addressComponents),
    formatted_address: place.formattedAddress || "",
    place_id: place.id || "",
    display_name: place.displayName || "",
    location: loc
      ? {
          lat: typeof loc.lat === "function" ? loc.lat() : loc.lat,
          lng: typeof loc.lng === "function" ? loc.lng() : loc.lng,
        }
      : null,
  };
};

// Inject the element's CSS-part styling once so it visually matches the
// app's existing inputs (h-[55px], rounded-lg, gray border).
let stylesInjected = false;
const injectStylesOnce = () => {
  if (stylesInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.dataset.gmpPlaceAutocomplete = "true";
  style.textContent = `
    gmp-place-autocomplete {
      width: 100%;
      display: block;
    }
    gmp-place-autocomplete::part(input) {
      width: 100%;
      height: 55px;
      padding: 0 16px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      font-size: 14px;
      background: #fff;
      color: #111827;
      outline: none;
      box-sizing: border-box;
    }
    gmp-place-autocomplete::part(input):focus {
      border-color: #586989;
    }
  `;
  document.head.appendChild(style);
  stylesInjected = true;
};

export default function PlaceAutocomplete({
  placeholder = "Search address",
  regionCodes,
  onSelect,
  className = "",
  disabled = false,
}) {
  const wrapperRef = useRef(null);
  const elementRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  useEffect(() => {
    if (!isLoaded || !wrapperRef.current) return;
    const PlaceAutocompleteElement =
      window.google?.maps?.places?.PlaceAutocompleteElement;
    if (!PlaceAutocompleteElement) return;

    injectStylesOnce();

    const el = new PlaceAutocompleteElement();
    elementRef.current = el;
    if (placeholder) el.placeholder = placeholder;
    if (regionCodes && regionCodes.length) {
      try {
        el.includedRegionCodes = regionCodes;
      } catch (err) {
        console.warn("PlaceAutocomplete: unable to set region codes", err);
      }
    }
    if (disabled) el.setAttribute("disabled", "");

    const handleGmpSelect = async (event) => {
      const prediction = event?.placePrediction;
      if (!prediction) return;
      try {
        const place = prediction.toPlace();
        await place.fetchFields({
          fields: [
            "displayName",
            "formattedAddress",
            "location",
            "id",
            "addressComponents",
          ],
        });
        onSelectRef.current?.(normalizePlace(place));
      } catch (err) {
        console.error("PlaceAutocomplete: fetchFields failed", err);
      }
    };

    el.addEventListener("gmp-select", handleGmpSelect);
    wrapperRef.current.appendChild(el);

    return () => {
      el.removeEventListener("gmp-select", handleGmpSelect);
      if (el.parentNode) el.parentNode.removeChild(el);
      elementRef.current = null;
    };
    // We intentionally mount once when the API becomes available. Dynamic
    // props are propagated via the effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  const regionCodesKey =
    regionCodes && regionCodes.length ? regionCodes.join(",") : "";
  useEffect(() => {
    if (!elementRef.current) return;
    if (!regionCodesKey) return;
    try {
      elementRef.current.includedRegionCodes = regionCodesKey.split(",");
    } catch (_) {}
  }, [regionCodesKey]);

  useEffect(() => {
    if (!elementRef.current) return;
    if (placeholder != null) elementRef.current.placeholder = placeholder;
  }, [placeholder]);

  useEffect(() => {
    if (!elementRef.current) return;
    if (disabled) elementRef.current.setAttribute("disabled", "");
    else elementRef.current.removeAttribute("disabled");
  }, [disabled]);

  if (loadError) {
    return (
      <div className={`text-xs text-red-600 ${className}`}>
        Couldn't load address search. Please refresh the page.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        className={`w-full h-[55px] px-4 border rounded-lg bg-gray-50 flex items-center text-sm text-gray-400 ${className}`}
      >
        Loading address search…
      </div>
    );
  }

  return <div ref={wrapperRef} className={`w-full ${className}`} />;
}
